import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Binary,
  Bot,
  Boxes,
  Building2,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  Cpu,
  Database,
  FlaskConical,
  FolderTree,
  Github,
  KeyRound,
  Layers,
  LayoutDashboard,
  LogOut,
  MessageSquareText,
  MessagesSquare,
  ScrollText,
  ThumbsUp,
  ShieldCheck,
  Users,
  Moon,
  Sun,
  Wrench,
} from "lucide-react";
import { useTheme } from "@/lib/theme";
import { Link } from "@/components/ui/link";
import { usePathname, useRouter } from "@/lib/navigation";
import * as React from "react";
import { Outlet } from "react-router-dom";

import { Brand } from "@/components/brand";
import { Button, Skeleton } from "@/components/ui/primitives";
import { listMyOrganizations, logout, switchOrganization } from "@/lib/api/endpoints";
import { forceLogout } from "@/lib/api/session";
import { toast } from "@/components/ui/toaster";
import { useMe } from "@/lib/query/hooks";
import { cn } from "@/lib/utils";
import type { AdminRole } from "@/types/api";

interface NavItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: AdminRole[];
}

/** Who may build: org admins and engineers. */
const BUILDERS: AdminRole[] = ["tenant_admin", "engineer"];
/** Org policy — org admins only. */
const ORG_ADMIN: AdminRole[] = ["tenant_admin"];
/** Roles whose product is the chat app alone. */
const CHAT_ONLY: AdminRole[] = ["user", "evaluator"];

const NAV: NavItem[] = [
  {
    href: "/dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    roles: ["superadmin", ...BUILDERS],
  },
  // Superadmin — platform operations
  { href: "/tenants", label: "Tenants", icon: Building2, roles: ["superadmin"] },
  { href: "/model-catalog", label: "Model catalog", icon: Boxes, roles: ["superadmin"] },
  // Admins + engineers — building & running agents
  { href: "/domains", label: "Domains", icon: FolderTree, roles: BUILDERS },
  { href: "/agents", label: "Agents", icon: Bot, roles: BUILDERS },
  { href: "/capabilities", label: "Tools", icon: Wrench, roles: BUILDERS },
  { href: "/knowledge-bases", label: "Knowledge Base", icon: Database, roles: BUILDERS },
  { href: "/llm-models", label: "LLM Providers", icon: Cpu, roles: BUILDERS },
  { href: "/embedding-models", label: "Embedding models", icon: Binary, roles: BUILDERS },
  { href: "/vector-stores", label: "Vector stores", icon: Layers, roles: BUILDERS },
  { href: "/audit-logs", label: "Audit logs", icon: ScrollText, roles: BUILDERS },
  { href: "/evaluations", label: "Evaluations", icon: ThumbsUp, roles: BUILDERS },
  { href: "/playground", label: "Playground", icon: FlaskConical, roles: BUILDERS },
  { href: "/chat", label: "Chat", icon: MessagesSquare, roles: BUILDERS },
  // Org admins — people and policy
  { href: "/users", label: "Users", icon: Users, roles: ORG_ADMIN },
  { href: "/conversations", label: "Conversations", icon: MessageSquareText, roles: ORG_ADMIN },
  { href: "/observability", label: "Observability", icon: Activity, roles: ORG_ADMIN },
  {
    href: "/governance/security",
    label: "Repo Governance",
    icon: ShieldCheck,
    roles: BUILDERS,
  },
  {
    href: "/trusted-systems",
    label: "Trusted systems",
    icon: KeyRound,
    roles: ORG_ADMIN,
  },
  { href: "/github", label: "GitHub", icon: Github, roles: BUILDERS },
];

/**
 * The authenticated shell — and the session gate for every route inside it.
 *
 * This is what replaced `middleware.ts` (ADR-017). The middleware only ever
 * checked that the session *cookie was present*; the authoritative check —
 * resolving /me and reacting to a failure or a pending forced password change —
 * already lived here, so the gate did not move, only the cheap pre-check in
 * front of it went away. The visible difference is that an unauthenticated
 * visitor now sees this skeleton for one request instead of being redirected
 * before the app loads.
 */
export default function AppLayout() {
  const router = useRouter();
  const { data: me, isLoading, isError } = useMe();

  React.useEffect(() => {
    // A failed /me is an expired/invalid session. forceLogout clears the cookie
    // server-side before redirecting — a bare navigate would leave the stale
    // cookie in place, and the next /me would fail exactly the same way.
    if (isError) void forceLogout("expired");
    else if (me?.must_change_password) router.replace("/change-password");
    // Users and evaluators have no console: their whole product is the chat app.
    else if (me && CHAT_ONLY.includes(me.role)) router.replace("/chat");
  }, [isError, me, router]);

  if (isLoading) return <ShellSkeleton />;
  if (isError || !me || me.must_change_password || CHAT_ONLY.includes(me.role)) {
    return <ShellSkeleton />;
  }

  const items = NAV.filter((n) => n.roles.includes(me.role));

  return (
    <div className="flex min-h-screen bg-muted/20">
      <Sidebar items={items} role={me.role} />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar email={me.email} role={me.role} />
        <main className="mx-auto w-full max-w-6xl flex-1 px-6 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const COLLAPSED_KEY = "aviary.sidebar.collapsed";

/** Whether the sidebar is collapsed to an icon rail — remembered per browser.
 *  Storage can be unavailable (private windows, blocked site data), so every
 *  access is guarded and the sidebar simply starts expanded. */
function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = React.useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  React.useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      // Not remembered this time; the toggle still works.
    }
  }, [collapsed]);
  const toggle = React.useCallback(() => setCollapsed((c) => !c), []);
  return [collapsed, toggle];
}

function Sidebar({ items, role }: { items: NavItem[]; role: AdminRole }) {
  const pathname = usePathname();
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  return (
    // Pinned to the viewport: it used to scroll away with the page, taking the
    // organisation switcher at its foot off-screen on any long page. Only the
    // nav list scrolls (when there are more items than fit), so the brand and
    // the switcher are always in view.
    <aside
      className={cn(
        "sticky top-0 hidden h-screen shrink-0 flex-col self-start border-r border-border bg-card transition-[width] duration-200 md:flex",
        collapsed ? "w-16" : "w-60",
      )}
    >
      <div
        className={cn(
          "flex h-16 shrink-0 items-center border-b border-border",
          collapsed ? "justify-center px-2" : "justify-between gap-2 pl-5 pr-3",
        )}
      >
        {/* Collapsed, the rail is one icon wide: the toggle takes the logo's
            place rather than stacking a second row into the header. */}
        {collapsed ? null : <Brand />}
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
        </button>
      </div>
      <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-3">
        {items.map((item) => {
          const active = pathname === item.href || pathname.startsWith(`${item.href}/`);
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              href={item.href}
              // Icon-only when collapsed: the name moves to the tooltip and the
              // accessible name, so the rail is still navigable by label.
              title={collapsed ? item.label : undefined}
              aria-label={collapsed ? item.label : undefined}
              className={cn(
                "flex items-center gap-3 rounded-md py-2 text-sm font-medium transition-colors",
                collapsed ? "justify-center px-0" : "px-3",
                active
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {collapsed ? null : item.label}
            </Link>
          );
        })}
      </nav>
      {role !== "superadmin" ? <OrgSwitcher collapsed={collapsed} /> : null}
    </aside>
  );
}

function Topbar({ email, role }: { email: string; role: AdminRole }) {
  const router = useRouter();
  const qc = useQueryClient();
  const { theme, setTheme } = useTheme();
  const signOut = useMutation({
    mutationFn: logout,
    onSettled: async () => {
      qc.clear();
      router.replace("/login");
      router.refresh();
    },
  });

  return (
    <header className="flex h-16 items-center justify-between gap-4 border-b border-border bg-card px-6">
      <div className="md:hidden">
        <Brand compact />
      </div>
      <div className="ml-auto flex items-center gap-3">
        <div className="hidden text-right sm:block">
          <div className="text-sm font-medium text-foreground">{email}</div>
          <div className="text-xs capitalize text-muted-foreground">{role.replace("_", " ")}</div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Toggle theme"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
        >
          <Sun className="h-4 w-4 dark:hidden" />
          <Moon className="hidden h-4 w-4 dark:block" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={signOut.isPending}
          onClick={() => signOut.mutate()}
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </Button>
      </div>
    </header>
  );
}

function ShellSkeleton() {
  return (
    <div className="flex min-h-screen">
      <div className="hidden w-60 border-r border-border bg-card p-4 md:block">
        <Skeleton className="mb-6 h-9 w-32" />
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      </div>
      <div className="flex-1 p-8">
        <Skeleton className="mb-6 h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
}

/** Organisation switcher — sidebar footer, styled as sidebar chrome.
 *
 * A bare ``<select>`` in the top bar read as a stray form control dropped onto
 * the header: browser-chrome borders, its own font, nothing else around it
 * shaped like an input. So the visible part is built from the same pieces as a
 * nav item — muted text, the same radius and padding, ``hover:bg-muted`` — and
 * the real ``<select>`` sits transparently on top of it.
 *
 * That layering is deliberate rather than a custom popover: the native element
 * keeps keyboard handling, focus, screen-reader semantics and the platform's
 * own dropdown, and only its appearance is replaced. ``opacity-0`` rather than
 * ``appearance-none`` because Safari and Firefox still paint parts of a select
 * that ``appearance-none`` does not remove.
 *
 * Rendered only when the session actually has a choice. The list is the
 * server's *proven* set — organisations whose password was verified at this
 * login — not every tenant sharing the email; only the first is safe to offer
 * as a one-click switch.
 *
 * Switching re-issues the session cookie server-side, so every cached query
 * belongs to the previous organisation and has to go.
 */
function OrgSwitcher({ collapsed }: { collapsed: boolean }) {
  const qc = useQueryClient();
  const router = useRouter();
  const orgs = useQuery({
    queryKey: ["me", "organizations"],
    queryFn: listMyOrganizations,
    staleTime: 5 * 60 * 1000,
  });

  const move = useMutation({
    mutationFn: switchOrganization,
    onSuccess: async () => {
      // Everything on screen was fetched against the old tenant's database.
      qc.clear();
      router.replace("/dashboard");
      router.refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const available = orgs.data ?? [];
  // One organisation is not a choice — don't render a control that can't act.
  if (available.length < 2) return null;

  const current = available.find((o) => o.current);

  return (
    <div className="border-t border-border p-3">
      {collapsed ? null : (
        <span className="px-3 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          Organisation
        </span>
      )}
      <div className="relative mt-1">
        <div
          className={cn(
            "flex items-center gap-3 rounded-md py-2 text-sm font-medium transition-colors",
            collapsed ? "justify-center px-0" : "px-3",
            "text-foreground",
            move.isPending ? "opacity-60" : "hover:bg-muted",
          )}
        >
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          {collapsed ? null : (
            <>
              <span className="truncate">{current?.display_name ?? "Select…"}</span>
              <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
            </>
          )}
        </div>
        {/* The real control: invisible, exactly covering the block above, so
            the native dropdown opens where the label is.

            The colours matter despite `opacity-0`. The open dropdown is painted
            by the browser, not the page, and it takes its palette from this
            element's own `color` / `background-color` — opacity applies to the
            element, not to the popup. Left unset, the popup came out with a
            white background while inheriting this sidebar's light foreground:
            white text on white. Setting the popover tokens here, and again on
            the options for the browsers that don't inherit them, gives the
            dropdown the same palette as the rest of the app in both themes. */}
        <select
          aria-label="Organisation"
          // Collapsed, the org name is only visible on hover.
          title={collapsed ? `Organisation: ${current?.display_name ?? ""}` : undefined}
          value={current?.slug ?? ""}
          disabled={move.isPending}
          onChange={(e) => {
            if (e.target.value && e.target.value !== current?.slug) move.mutate(e.target.value);
          }}
          className="absolute inset-0 h-full w-full cursor-pointer bg-popover text-popover-foreground opacity-0"
        >
          {available.map((o) => (
            <option key={o.slug} value={o.slug} className="bg-popover text-popover-foreground">
              {o.display_name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
